import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import { Btn } from '../components/Btn';
import { Input } from '../components/Field';
import { Logo } from '../components/Logo';
import { PasswordStrengthMeter, usePasswordRules } from '../components/PasswordStrengthMeter';
import type { HttpError } from '../api/client';

export function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>();
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const { allValid: passwordValid } = usePasswordRules(newPassword);

  const reset = useMutation({
    mutationFn: () => authApi.resetPassword(token!, newPassword),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setConfirmError(null);
    if (!passwordValid) {
      setConfirmError('Hasło nie spełnia wszystkich wymagań.');
      return;
    }
    if (newPassword !== confirm) {
      setConfirmError('Powtórzone hasło nie pasuje.');
      return;
    }
    reset.mutate();
  };

  const apiErr = reset.isError ? (reset.error as HttpError).body?.message ?? 'Nie udało się zresetować hasła.' : null;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#fbfaf6',
        color: '#0f1e3d',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
      }}
    >
      <form
        onSubmit={onSubmit}
        style={{ width: '100%', maxWidth: 420 }}
      >
        <div style={{ marginBottom: 28 }}>
          <Logo size={56} />
        </div>

        <div
          style={{
            fontSize: 12,
            color: '#5b6578',
            letterSpacing: '.08em',
            fontWeight: 600,
            textTransform: 'uppercase',
          }}
        >
          Reset hasła
        </div>
        <h2 className="serif" style={{ fontSize: 32, fontWeight: 700, margin: '6px 0 4px', letterSpacing: '-0.02em' }}>
          Ustaw nowe hasło
        </h2>
        <div style={{ fontSize: 14, color: '#5b6578', marginBottom: 28 }}>
          Wybierz nowe hasło spełniające wymagania bezpieczeństwa.
        </div>

        {reset.isSuccess ? (
          <div
            style={{
              padding: 14,
              background: 'rgba(31,157,85,.1)',
              color: '#0f6b34',
              borderRadius: 10,
              fontSize: 13,
              marginBottom: 18,
              lineHeight: 1.5,
            }}
          >
            Hasło zostało zmienione.{' '}
            <Link to="/login" style={{ color: '#0f6b34', fontWeight: 600 }}>
              Zaloguj się
            </Link>
            .
          </div>
        ) : (
          <>
            <label style={fieldLabel}>NOWE HASŁO</label>
            <Input
              type="password"
              required
              minLength={12}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />

            <div style={{ marginBottom: 14 }}>
              <PasswordStrengthMeter password={newPassword} />
            </div>

            <label style={fieldLabel}>POWTÓRZ NOWE HASŁO</label>
            <Input
              type="password"
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              style={{ marginBottom: 18 }}
            />

            {(confirmError || apiErr) && (
              <div
                style={{
                  padding: 12,
                  background: 'rgba(208,70,60,.08)',
                  color: '#8a2a22',
                  borderRadius: 10,
                  fontSize: 13,
                  marginBottom: 14,
                }}
              >
                {confirmError ?? apiErr}
              </div>
            )}

            <Btn
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={reset.isPending || !passwordValid || newPassword !== confirm}
            >
              {reset.isPending ? 'Zapisywanie…' : 'Zapisz nowe hasło'}
            </Btn>
          </>
        )}

        <div style={{ marginTop: 18, textAlign: 'center', fontSize: 13 }}>
          <Link to="/login" style={{ color: '#5b6578', textDecoration: 'none' }}>
            Wróć do logowania
          </Link>
        </div>
      </form>
    </div>
  );
}

const fieldLabel = {
  fontSize: 11,
  color: '#5b6578',
  fontWeight: 600,
  letterSpacing: '.08em',
  display: 'block',
  marginBottom: 6,
};
